import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import TabPanel from "@mui/lab/TabPanel";
import "./SnSheet.css"; // 커스텀 CSS 추가
import {
  createColumnDefs,
  getCellStyle,
  getTooltipValueGetter,
} from "./columnCreate";
import { ColumnConfig, CustomColDef } from "./types";
import { menuInfo } from "@shared/type/CommonType";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  ModuleRegistry,
  ExcelExportModule,
  RowGroupingModule,
  ClientSideRowModelModule,
  GridChartsModule,
  ColDef,
  EditableCallbackParams, // 통합 차트 모듈
  GridApi,
  GridOptions,
  ColumnState,
  CellKeyDownEvent,
  Column,
  NavigateToNextCellParams,
  ColGroupDef,
  RowNode,
  CellFocusedEvent,
  GetMainMenuItemsParams,
  MenuItemDef,
  RowSelectionOptions,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
  IRowNode,
  DefaultMenuItem,
  ProcessDataFromClipboardParams,
} from "ag-grid-enterprise";
import { v4 as uuidv4 } from "uuid";
import { global } from "@shared/libs/Global";
import { mapExcelUploadTable, takeExcelUploadTable } from "@shared/libs/excelUploadOffer";
import { getDate } from "date-fns";
import { showDialog } from "src/widgets/CommonDialog";
import SnTab from "@shared/components/SnTab";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { showPopupModal } from "@widgets/CommonModal";
import {
  getSearchData,
  getInitPopUp,
  getCodeData,
  isDateByFormat,
  parseToyyyyMMdd,
  parseToyyyyMMddSnSheet,
} from "@shared/utils/CommonUtils";
import { StyleSettings } from "../../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import SnTabHeader from "../SnTabHeader";
// 필요한 모듈 등록
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ExcelExportModule,
  RowGroupingModule,
  GridChartsModule,
]);

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

//#region 우클릭 메뉴 한글화 작업

const localeText = {
  sortAscending: "오름차순 정렬",
  sortDescending: "내림차순 정렬",

  // 컬럼 크기 조정
  autosizeThisColumn: "이 열 크기 자동 조정",
  autosizeAllColumns: "모든 열 크기 자동 조정",

  // 컬럼 선택
  pivotMode: "피벗 모드",

  toolPanelColumns: "컬럼 선택",
  chooseColumns: "항목 복원",
  columnChooser: "항목 복원",

  // 초기화
  resetColumns: "열 초기화",

  // 기타 기본 키
  pinColumn: "컬럼 고정",

  // 컬럼 고정 메뉴
  pinLeft: "왼쪽에 고정", // Pin Left
  pinRight: "오른쪽에 고정", // Pin Right
  noPin: "고정 해제", // No Pin

  csvExport: "CSV 내보내기", // CSV Export
  excelExport: "Excel 내보내기", // Excel Export

  copy: "복사",
  copyWithHeaders: "헤더 포함 복사",
  copyWithGroupHeaders: "그룹 헤더 포함 복사",
  paste: "붙여넣기",
  export: "내보내기",
  filterOoo: "필터...",
  equals: "같음",
  notEqual: "같지 않음",
  lessThan: "미만",
  greaterThan: "초과",
  inRange: "범위 내",
  groupBy: "그룹화",
  ungroupAll: "모든 그룹 해제",
  expandAll: "전체 확장",
  collapseAll: "전체 축소",
  autoSizeThis: "이 열 크기 자동 조정",
  chartRange: "차트 범위",
  categories: "범주",
  series: "시리즈",
  chartSettings: "차트 설정",
  chartType: "차트 유형",
  legend: "범례",
  axisLabels: "축 레이블",
  valueAxis: "값 축",
  categoryAxis: "범주 축",
  exportChart: "차트 내보내기",

  // 추가 번역 항목
  resetChart: "차트 초기화",
  groupedColumn: "그룹화된 열",
  stackedColumn: "스택 열",
  normalizedColumn: "정규화된 열",
  groupedBar: "그룹화된 막대",
  stackedBar: "스택 막대",
  normalizedBar: "정규화된 막대",
  pie: "원형 차트",
  doughnut: "도넛 차트",
  line: "선형 차트",
  scatter: "산점도",
  bubble: "버블 차트",
  area: "영역 차트",
  stackedArea: "스택 영역",
  normalizedArea: "정규화된 영역",
  columnChart: "열 차트",

  barChart: "막대 차트",

  pieChart: "원형 차트",

  lineChart: "선형 차트",
  xyChart: "X Y (산점도)",

  areaChart: "영역 차트",

  histogramChart: "히스토그램",
  rangeBarChart: "범위 막대",
  rangeAreaChart: "범위 영역",
  combinationChart: "조합 차트",

  polarChart: "극좌표 차트",
  radarLine: "방사형 선형 차트",
  radarArea: "방사형 영역 차트",
  nightingale: "나이팅게일 차트",
  radialColumn: "방사형 열 차트",
  radialBar: "방사형 막대 차트",

  statisticalChart: "통계 차트",
  boxPlot: "상자 그림",
  histogram: "히스토그램",
  rangeBar: "범위 막대",
  rangeArea: "범위 영역",

  hierarchicalChart: "계층형 차트",
  organizationChart: "조직도 차트",
  treeChart: "트리 차트",

  specializedChart: "특수 차트",
  heatMap: "히트맵",
  treemap: "트리맵",
  waterfall: "폭포 차트",

  // 조합 차트
  columnLineCombo: "열 & 선형 차트", // Column & Line
  areaColumnCombo: "영역 & 열 차트", // Area & Column

  // 계층형 차트
  sunburst: "선버스트", // Sunburst

  // 특수 차트
  heatmap: "히트맵",
};

//#endregion

//#region SnSheet Props

interface SnSheetProps {
  menuInfo?: menuInfo; // 메뉴코드 추가
  columnDefs?: ColDef[]; // 직접 전달된 컬럼 정의
  columnConfig?: ColumnConfig[]; // 컬럼 설정을 기반으로 생성할 경우
  rowData: RowData[] | null; // null 허용
  keyField?: string[]; // 고유 키로 사용할 필드들 (기본값: ["id"])
  gridOptions?: any; // AG Grid 옵션
  style?: React.CSSProperties; // 스타일 (선택적)
  onGridReady?: (params: any) => void; // 그리드 초기화 후 호출할 함수
  enableExternalChart?: boolean; // 독립 차트 활성화 여부
  chartColumns?: string[]; // 차트로 사용할 컬럼 리스트
  isEditable?: boolean; // 수정 가능 여부
  enableAddRow?: boolean; // 행 추가 활성화 여부
  rowSelection?: "single" | "multiple"; // 행 선택 모드

  pagination?: boolean; // Paging 여부
  paginationPageSize?: number; // Paging Size
  paginationPageSizeSelector?: any[];
  enableRangeSelection?: boolean; // 블록 지정 활성화
  rowGroupPanelShow?: "onlyWhenGrouping" | "never" | "always";
  animateRows?: boolean; // 행 애니메이션 활성화
  rowDragManaged?: boolean; // 행 드래그 활성화
  cellSelection?: boolean; // Cell 선택
  enableFillHandle?: boolean; // 블록 모서리를 드래그하면 같은 값으로 채우기 (편집 가능 칸만)
  suppressNoRowsOverlay?: boolean; // 그리드에 no rows to show 제거
  suppressMenuHide?: boolean; // Column Menu visilble
  ischeckboxSelection?: boolean; // 체크박스 셀렉션
  onRowClicked?: (event: any) => void; // row 클릭
  onCellValueChanged?: (event: any) => void; // 셀 값 변경 이벤트
  onCellDoubleClicked?: (event: any) => void; // 셀 dblclick  이벤트
  onBeforeAddRow?: (newRow: RowData) => boolean; // 행 추가 전 이벤트
  onBeforeDeleteRows?: (rowsToDelete: RowData[]) => boolean; // 행 삭제 전 이벤트
  onBeforeEdit?: (rowData: RowData, colDef: any) => boolean; // 셀 수정 전 이벤트
  onFocusedRowChanged?: (focusedRowData: RowData | null) => void;
  onRowDoubleClicked?: (e: any) => void;
  onSelectionChanged?: (selectedRows: any[]) => void; //  추가
  seq?: number; // 그리드 순번
  title?: string;
  onBtnAddClick?: (id: string) => void;
  onBtnDeleteClick?: (id: string) => void;
  header?: React.ReactNode;
  codeDataList: Record<string, string>[]; //  pk 체크용 데이터 (배열 형태)
  compareFields: string[]; //  비교할 필드 정보 (문자열 배열)
  getContextMenuItems?: (params: any) => any[];
}

//#endregion

const SnSheet = forwardRef((props: SnSheetProps, ref) => {
  // 구조 분해를 통해 props에서 필요한 속성 추출
  const {
    columnDefs, // 직접 전달된 columnDefs
    columnConfig, // columnConfig를 기반으로 columnDefs 생성
    menuInfo,
    rowData: initialRowData,
    keyField = ["KeyField"], // 기본적으로 "KeyField"를 고유 식별자로 사용
    gridOptions = {},
    style,
    onGridReady,
    enableExternalChart = false, // 독립 차트 기본값: 비활성화
    chartColumns = [], // 차트로 사용할 기본 컬럼 (없음)
    isEditable = false,
    enableAddRow = false,
    rowSelection: rowSelectionMode,
    pagination = false,
    paginationPageSize = 50,
    paginationPageSizeSelector = [50, 100],
    enableRangeSelection = true,
    rowGroupPanelShow = "onlyWhenGrouping",
    // rowGroupPanelShow = "always",
    animateRows = true,
    rowDragManaged = false,
    cellSelection = true,
    enableFillHandle = false,
    suppressNoRowsOverlay = true,
    suppressMenuHide = false,
    ischeckboxSelection = false, // 체크박스 셀렉션
    onRowClicked, // 로우 클릭
    onCellValueChanged, // 값 수정
    onCellDoubleClicked, // 셀더블클릭
    onBeforeAddRow, // 행 추가 전 이벤트
    onBeforeDeleteRows, // 행 삭제 전 이벤트
    onBeforeEdit, // 수정 전 이벤트
    onFocusedRowChanged, // 새로운 prop
    onRowDoubleClicked,
    onSelectionChanged, //  추가
    title,
    onBtnAddClick,
    onBtnDeleteClick,
    header,
    codeDataList, // pk 체크용
    compareFields,
  } = props;

  //#region KeyField 고유 키 생성 함수
  const usedKeys = new Set<string>(); // 이미 사용된 키를 추적
  const [errorMap, setErrorMap] = useState(new Map()); // 에러메시지
  const [checkCodeData, setcheckCodeData] =
    useState<Record<string, string>[]>(codeDataList); // pk 체크 대상 데이터 eg) mas_item, mas_cust..

  useEffect(() => {
    setcheckCodeData(codeDataList);
  }, [codeDataList]);

  // const navigate = useNavigate();
  // 전역 변수 선언

  let cdGrid: string | null = null;

  const generateKey = () => {
    let key;
    do {
      key = uuidv4(); // 항상 새로운 UUID 생성
    } while (usedKeys.has(key)); // 중복 여부 확인
    usedKeys.add(key);
    return key;
  };

  //#endregion
  const { userInfo } = global();
  // State로 columnDefs 관리
  const [resolvedColumnDefs, setResolvedColumnDefs] = useState<ColDef[]>([]);

  // editable 여부를 제어하는 함수
  const isEditableFunc = (field: string): boolean => {
    return isEditable && field !== "readonly_field";
  };

  // 이전 포커스된 행 데이터 저장용 Ref
  const previousFocusedRowRef = useRef<RowData | null>(null);

  // columnConfig 변경 시에만 createColumnDefs 실행
  const memoizedColumnConfig = useMemo(() => columnConfig, [columnConfig]);
  const [requireds, setRequireds] = useState<any>({}); /* */
  const [multipopups, setMultipopup] = useState<any>({}); /* */
  const [singlepopups, setSinglepopup] = useState<any>({}); /* */
  const [editCons, setEditCons] = useState<any>({}); /* */

  const [codeDataMap, setCodeDataMap] = useState(new Map());
  const [popupDataMap, setPopupDataMap] = useState(new Map());
  const codeDataCacheRef = useRef<Map<string, any[]>>(new Map());
  const popupDataCacheRef = useRef<Map<string, any[]>>(new Map());

  useEffect(() => {
    const loadColumnDefs = async () => {
      // console.log("==================loadColumnDefs.");
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (columnDefs) {
        // columnDefs가 직접 전달된 경우 그대로 설정
        updatedColumnDefs = columnDefs;
      } else if (memoizedColumnConfig) {
        // columnConfig가 변경된 경우에만 비동기 함수 호출

        // memoizedColumnConfig의 첫 번째 항목에서 cd_grid 추출
        cdGrid = memoizedColumnConfig[0]?.cd_grid || null;

        // 2025.01.08 필수값 체크용
        const requiredsFilter = memoizedColumnConfig.filter(
          (col) => col.yn_required == "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_column] = req.nm_column;
        });
        setRequireds(tempreq);

        const editConsFilter = memoizedColumnConfig.filter(
          (col) => col.dc_editcon != "" && col.dc_editcon != null
        );
        let tempeditCons = Object.assign({});
        editConsFilter.map((con) => {
          tempeditCons[con.cd_column] = con.dc_editcon;
        });
        setEditCons(tempeditCons);

        // 2025.01.14 팝업용
        const multiSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnMultiCodeBox"
        );
        setMultipopup(multiSetting);
        const singleSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnCodeBox"
        );

        setSinglepopup(singleSetting);

        const codeMap = new Map();
        const popupMap = new Map();

        //  cd_clas 데이터 로드 (중복 호출 방지)
        const uniqueCdClas = Array.from(
          new Set(
            memoizedColumnConfig
              .filter((col) => col.cd_clas && typeof col.cd_clas === "string")
              .map((col) => col.cd_clas as string)
          )
        );

        await Promise.all(
          uniqueCdClas.map(async (cd_clas) => {
            codeMap.set(cd_clas, await getCodeData(cd_clas, ""));
          })
        );

        // cd_popup 데이터 로드 (중복 호출 방지)
        const uniqueCdPopup = Array.from(
          new Set(
            memoizedColumnConfig
              .filter((col) => col.cd_popup && typeof col.cd_popup === "string")
              .map((col) => col.cd_popup as string)
          )
        );

        await Promise.all(
          uniqueCdPopup.map(async (cd_popup) => {
            popupMap.set(cd_popup, await getCodeData(cd_popup, ""));
          })
        );

        setCodeDataMap(codeMap);
        setPopupDataMap(popupMap);

        const generatedColumnDefs = await createColumnDefs(
          memoizedColumnConfig,
          isEditableFunc,
          userInfo,
          errorMap
          // ,          gridRefcol
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
          ...colDef,
          tooltipValueGetter: undefined,
          mainMenuItems: mainMenuItems, // 공통 커스텀 메뉴 추가
        }));
      }
      // checkboxSelection이 필요한 경우 컬럼 추가

      if (ischeckboxSelection) {
        updatedColumnDefs = [
          {
            headerName: "",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            suppressColumnsToolPanel: true,
            headerCheckboxSelectionFilteredOnly: true, //  현재 필터링된 행만 체크/언체크
            width: 50,
            colId: "fixCheckbox",
          },
          ...updatedColumnDefs,
        ];
      }
      // 드래그 할 수 있는 컬럼
      if (rowDragManaged) {
        updatedColumnDefs = [
          {
            headerName: "순서 변경",
            field: "rowDrag",
            rowDrag: true,
            editable: false,
            width: 50,
            colId: "fixDrag",
          },
          ...updatedColumnDefs,
        ];
      }
      setResolvedColumnDefs(updatedColumnDefs);
    };

    loadColumnDefs();
  }, [columnDefs, memoizedColumnConfig]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true, // 헤더 필터 비활성화
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      editable: isEditable, // 수정 가능 여부를 props로 제어
      headerClass: "header-center",
      cellClassRules: {
        "sn-error-cell": (p: any) => {
          if (!p?.node || p.node.rowPinned) return false;
          const rowIndex = p.node.rowIndex ?? -1;
          const colField = p.colDef?.field;
          if (!colField) return false;
          const info = errorMap.get(rowIndex);
          return !!(info && info.fields?.has(colField));
        },
      },
      tooltipValueGetter: getTooltipValueGetter(errorMap) as any,
    }),
    [isEditable, errorMap]
  );

  const columnSave = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;
      const columnState = gridRef.current?.api
        .getColumnState()
        .filter((colDef: any) => !colDef.colId?.startsWith("fix"));
      if (!columnState || columnState.length === 0) return;

      const updatedColumnDefs = memoizedColumnConfig.map((colDef: any) => {
        // columnState에서 동일한 colId 찾기
        const matchingColumn = columnState?.find(
          (state) => state.colId === colDef.cd_column
        );
        const matchingIndex = columnState?.findIndex(
          (state) => state.colId === colDef.cd_column
        );

        return {
          ...colDef,
          cd_user: userInfo.cd_user,
          qt_width: matchingColumn?.width ?? colDef.qt_width,
          no_seq: matchingIndex !== -1 ? matchingIndex : colDef.no_seq,
          fg_visible:
            matchingColumn?.hide !== undefined
              ? matchingColumn.hide
                ? "N"
                : "Y"
              : colDef.fg_visible,
        };
      });

      if (memoizedColumnConfig[0].cd_user !== userInfo.cd_user) {
        modifiedRows.current.added.push(...updatedColumnDefs);
      } else {
        modifiedRows.current.updated.push(...updatedColumnDefs);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      const preserveChanges =
        (gridRef.current?.api as any)?.__preserveChangesOnValidate ?? false;
      if (!preserveChanges) {
        resetUpdate();
      }
    } catch (error: any) {}
  };

  const columnInit = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;

      if (memoizedColumnConfig[0].cd_user === userInfo.cd_user) {
        modifiedRows.current.deleted.push(...memoizedColumnConfig);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      resetUpdate();
    } catch (error: any) {}
  };

  const mainMenuItems = (
    params: GetMainMenuItemsParams
  ): (MenuItemDef | string)[] => {
    const athleteMenuItems: (MenuItemDef | string)[] = [
      {
        name: "항목설정저장",
        action: () => {
          columnSave();
        },
      },
      {
        name: "항목 제거",
        action: () => {
          //const columnId = params.column.getColId();
          //gridRef.current?.api.setColumnVisible(columnId, false);
        },
      },
    ];
    //항목복원은 ag grid 기본기능(columnChooser)
    const columnChooseItem = params.defaultItems.find(
      (item) => item === "columnChooser"
    );
    if (columnChooseItem) {
      athleteMenuItems.push(columnChooseItem);
    }
    athleteMenuItems.push(
      {
        name: "항목 초기화",
        action: () => {
          columnInit();
        },
      },

      ...params.defaultItems.filter(
        (_, index) => ![0, 1, 9, 10, 11].includes(index)
      )
    );
    athleteMenuItems.push(
      "separator",
      {
        name: "필터/ 필터해제",
        action: () => {
          const headerIcons = document.querySelectorAll(
            ".ag-header-cell .ag-header-icon"
          );

          headerIcons.forEach((icon) => {
            icon.classList.toggle("hidden-filter"); // 필터 아이콘을 숨기거나 보이게 토글
          });
        },
      },
      {
        name: "찾기",
        action: () => {
          setIsFilterVisible(true);
          const filterTextBox = document.getElementById(
            "filter-text-box"
          ) as HTMLInputElement;
          filterTextBox?.focus(); // 필터 텍스트 박스에 포커스 설정
        },
      },
      {
        name: "순서변경",
        action: () => {
          setTimeout(() => {
            enableRowDrag();
          }, 100);
        },
      }
    );
    return athleteMenuItems;
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 200,
      suppressMenu: false,
      cellRendererParams: {
        innerRenderer: (params: any) => {
          return params.value;
        },
      },
      mainMenuItems: (
        params: GetMainMenuItemsParams
      ): (MenuItemDef | DefaultMenuItem)[] => {
        const customMenuItems: MenuItemDef[] = [
          {
            name: "모든 그룹 해제",
            action: () => {
              params.api.setRowGroupColumns([]); // 모든 그룹 해제
            },
          },
          {
            name: "전체 확장",
            action: () => {
              params.api.expandAll(); // 전체 확장
            },
          },
          {
            name: "전체 축소",
            action: () => {
              params.api.collapseAll(); // 전체 축소
            },
          },
        ];

        // ✅ 필요하다면 기본 메뉴 항목도 추가 가능
        // return ['pinSubMenu', 'separator', ...customMenuItems];
        return customMenuItems;
      },
    };
  }, []);

  //#endregion
  const enableRowDrag = () => {
    if (gridRef.current?.api) {
      console.log(" 1. 우클릭 메뉴에서 rowDrag 활성화 시작");

      const updatedRows: any[] = [];

      gridRef.current.api.forEachNode((node) => {
        if (!node.data.rowDrag) {
          updatedRows.push({ ...node.data, rowDrag: true });
        }
      });

      if (updatedRows.length > 0) {
        console.log(" 업데이트할 행:", updatedRows);
        setTimeout(() => {
          gridRef.current?.api.refreshCells({ force: true });
          gridRef.current?.api.redrawRows();
          console.log("🔄 그리드 UI 강제 업데이트 완료!");
        }, 100);
      }
    }
  };

  //  [ADD] 복사(Ctrl+C) 시 표시값을 클립보드로 보내기
  const processCellForClipboard = (params: any) => {
    const colDef = params.column?.getColDef?.() as any;
    if (!colDef) return params.value;

    // SnComboBox / SnCodeBox 류면 표시 컬럼 우선 반환
    if (colDef.tp_column === "SnComboBox" || colDef.tp_column === "SnCodeBox") {
      const displayField =
        colDef.cd_displaycolumn ||
        (colDef.field ? colDef.field.replace(/^cd_/, "nm_") : null);
      if (
        displayField &&
        params.node?.data &&
        params.node.data[displayField] != null
      ) {
        return params.node.data[displayField]; // "이름"을 복사
      }
    }
    return params.value; // 기본 동작
  };

  //  [ADD] 엑셀 내보내기 시에도 표시값으로 나가도록 처리
  const processCellForExport = (params: any) => {
    const colDef = params.column?.getColDef?.() as any;
    if (!colDef) return params.value;

    if (colDef.tp_column === "SnComboBox" || colDef.tp_column === "SnCodeBox") {
      const displayField =
        colDef.cd_displaycolumn ||
        (colDef.field ? colDef.field.replace(/^cd_/, "nm_") : null);
      if (
        displayField &&
        params.node?.data &&
        params.node.data[displayField] != null
      ) {
        return params.node.data[displayField];
      }
    }
    return params.value;
  };

  //#region 초기화

  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조

  const [isGridReady, setIsGridReady] = useState(false);

  // Generate a unique storage key based on URL and component ID
  const getStorageKey = () => {
    // debugger;
    const baseKey = menuInfo?.cd_menu; // Current page path

    const componentId = cdGrid || "defaultGrid"; // gridId가 없으면 기본값 사용
    return `gridColumnState_${baseKey}_${componentId}`;
  };

  // Save column state to local storage
  const saveColumnState = () => {
    //debugger;
    if (gridRef.current?.api) {
      const columnState = gridRef.current.api.getColumnState();
      const key = getStorageKey();

      const previousState = localStorage.getItem(key);

      if (
        previousState &&
        JSON.stringify(JSON.parse(previousState)) ===
          JSON.stringify(columnState)
      ) {
        console.log("Column state unchanged. Skipping save.");
        return; // 저장하지 않음
      }

      localStorage.setItem(key, JSON.stringify(columnState));

      console.log(`Column state save :`, columnState);
    }
  };

  // Restore column state from local storage
  const restoreColumnState = () => {
    console.log(`******restoreColumnState`);
    if (gridRef.current?.api) {
      const columnState = localStorage.getItem(getStorageKey());
      if (columnState && gridRef.current?.api) {
        const parsedState = JSON.parse(columnState);

        gridRef.current.api.applyColumnState({
          state: parsedState,
          applyOrder: true,
        });
        console.log(`Column state restored:`, parsedState);
      } else {
        console.log(
          `No column state found for key: ${getStorageKey()}. Using defaults.`
        );
      }
    }
  };

  // Debounce 적용
  let debounceTimeout: number;

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    return (...args: any[]) => {
      clearTimeout(debounceTimeout); // timeout을 해제
      debounceTimeout = window.setTimeout(() => func(...args), delay); // setTimeout 사용
    };
  };

  // 데이터 초기화

  const [rowData, setRowData] = useState<RowData[]>(
    (initialRowData || []).map((row) => {
      const key = row.KeyField || generateKey();
      return { ...row, KeyField: key };
    })
  );

  useEffect(() => {
    if (isGridReady && initialRowData) {
      setRowData(
        initialRowData.map((row) => ({
          ...row,
          KeyField: row.KeyField || generateKey(),
        }))
      );
      // restoreColumnState(); // 컬럼 상태 복원
    }
  }, [isGridReady, initialRowData]);

  // 변경된 행 상태 추적
  const modifiedRows = useRef({
    added: [] as RowData[],
    updated: [] as RowData[],
    deleted: [] as RowData[],
  });
  //#endregion

  //#region Footer 관련 내용
  // 하단 고정 행 데이터를 관리하는 상태
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  //#region Footer 합계 계산

  const formatNumber = (value: number) =>
    value ? Number(value).toLocaleString() : "0";

  const computeFooterSummary = () => {
    // debugger;
    if (!gridRef.current?.api) return;

    const renderedNodes = gridRef.current.api.getRenderedNodes();
    const renderedData = renderedNodes.map((node) => node.data);

    // resolvedColumnDefs를 CustomColDef로 타입 단언
    const customColumnDefs = resolvedColumnDefs as CustomColDef[];

    // yn_sum === "Y"인 컬럼 필터링
    const targetFields = customColumnDefs
      .filter((col) => col.yn_sum === "Y") // yn_sum이 "Y"인 컬럼만
      .map((col) => col.field) // 필드 이름만 추출
      .filter((field): field is string => !!field); // 유효한 필드만 유지

    const totalRow = targetFields.reduce(
      (acc, field) => {
        const sum = renderedData.reduce(
          (sum, row) => sum + (row[field] || 0),
          0
        );
        acc[field] = sum;
        return acc;
      },
      {} as Record<string, number>
    );

    if (JSON.stringify(pinnedBottomRowData[0]) !== JSON.stringify(totalRow)) {
      setPinnedBottomRowData([totalRow]);
    }
  };

  // //#endregion

  // 초기화 및 데이터 변경 시 합계 계산
  useEffect(() => {
    //    console.log("==================useEffect calculateFooter.");
    computeFooterSummary();
  }, [rowData]);

  const getFooterDataAsync = () => {
    return new Promise((resolve) => {
      computeFooterSummary();
      setTimeout(() => resolve(pinnedBottomRowData[0]), 0); // 상태 업데이트 대기
    });
  };

  // 특정 필드의 푸터 값 가져오기
  const getFooterData = () => {
    const footer = pinnedBottomRowData.length > 0 ? pinnedBottomRowData[0] : {};
    return new Proxy(footer, {
      get: (target, prop: string) => (prop in target ? target[prop] : null),
    });
  };

  //#region 1. 행 추가

  const getFirstEditableColumn = (columnDefs: ColDef[], defaultColDef: any) => {
    return columnDefs.find((col) => {
      if (col.editable === true) return true; // 명시적으로 editable이 true인 경우
      if (col.editable === false) return false; // editable이 false면 제외
      if (typeof col.editable === "function") {
        // EditableCallbackParams 타입의 더미 데이터를 제공
        const params: EditableCallbackParams = {
          node: {} as any, // 필요에 따라 노드 객체를 설정하거나 빈 객체로 처리
          data: {}, // row data
          column: {} as any, // 현재 컬럼 정보
          colDef: col, // 현재 컬럼 정의
          api: {} as any, // ag-Grid API 객체
          context: {}, // 그리드의 context 정보
        };

        // editable 함수 실행 결과 확인
        return col.editable(params);
      }
      return defaultColDef?.editable === true; // editable이 undefined면 defaultColDef를 참조
    })?.field;
  };

  const getSelectedRow = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    // return selectedRows && selectedRows.length > 0 ? selectedRows[0] : null;
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const getSelectedRows = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const addRow = (newRow: RowData = {}) => {
    // debugger;

    console.log("Before onBeforeAddRow:", newRow);

    if (onBeforeAddRow && onBeforeAddRow(newRow) === false) {
      console.log("Add row cancelled by onBeforeAddRow");
      return;
    }
    console.log("After  onBeforeAddRow:", newRow);

    const newKey = generateKey();
    // const newData = { KeyField: newKey, ...newRow };
    const newData = { ...newRow, KeyField: newKey, state: "insert" };

    // 1. rowData 업데이트

    modifiedRows.current.added.push(newData); // 추가된 행 기록

    gridRef.current?.api.applyTransaction({ add: [newData] });

    setRowData((prev) => [...prev, newData]); // 데이터 추가

    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) {
        console.error("Ag-Grid API is not available."); // 디버깅 로그
        return;
      }

      const newRowIndex = api.getDisplayedRowCount() - 1;

      const firstEditableCol =
        resolvedColumnDefs.find((col) => col.editable)?.field ||
        resolvedColumnDefs[0]?.field;

      if (newRowIndex !== undefined && firstEditableCol) {
        api.ensureIndexVisible(newRowIndex);
        api.setFocusedCell(newRowIndex, firstEditableCol);
        api.getDisplayedRowAtIndex(newRowIndex)?.setSelected(true);
        // 강제로 onCellFocused 트리거
        const focusedEvent: CellFocusedEvent = {
          type: "cellFocused",
          api: api,
          column: api.getColumn(firstEditableCol)!,
          rowIndex: newRowIndex,
          context: undefined,
          rowPinned: undefined,
        };
        handleCellFocused(focusedEvent); // 기존 핸들러 호출
      }
    }, 50); // 추가 후 약간의 지연
  };

  //#endregion

  //#region  2. 선택된 행 삭제

  const handleRowFocusAfterDelete = (
    deletedRowIndex: number,
    gridApi: GridApi
  ) => {
    const remainingRows = gridApi.getDisplayedRowCount();

    if (remainingRows === 0) {
      console.warn("남아 있는 행이 없습니다.");
      return;
    }

    // 삭제된 행의 이전 행으로 이동
    let rowIndexToFocus = deletedRowIndex - 1;
    if (rowIndexToFocus < 0) {
      rowIndexToFocus = 0; // 첫 번째 행으로 이동
    }

    const rowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (!rowNode) {
      rowIndexToFocus = deletedRowIndex; // 다음 행으로 이동
    }

    const focusedRowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (focusedRowNode) {
      focusedRowNode.setSelected(true); // 행 선택
      gridApi.ensureIndexVisible(rowIndexToFocus); // 행 가시화

      // 첫 번째 수정 가능한 컬럼에 포커스 설정
      const firstEditableCol = resolvedColumnDefs.find(
        (col) => col.editable
      )?.field;
      if (firstEditableCol) {
        gridApi.setFocusedCell(rowIndexToFocus, firstEditableCol);
      }
    }
  };

  const deleteSelectedRows = () => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();

    if (!selectedNodes || selectedNodes.length === 0) {
      console.warn("삭제할 행을 선택하세요.");
      return;
    }

    const selectedKeys = selectedNodes.map((node) => node.data.KeyField);

    const rowsToDelete = rowData.filter((row) =>
      selectedKeys.includes(row.KeyField)
    );

    // 삭제된 행의 첫 번째 노드의 인덱스
    const firstSelectedIndex = selectedNodes[0]?.rowIndex || 0;
    const rowsToRemoveIndexes = selectedNodes.map((node) => node.rowIndex);
    const rowsToRemove = selectedNodes.map((node) => node.data); // 데이터만 추출
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    // 상태 업데이트
    // setRowData((prev) =>
    //   prev.filter((row) => !selectedKeys.includes(row.KeyField))
    // );
    const currentData: RowData[] = [];
    gridRef.current?.api.forEachNode((node) => {
      if (node.data) currentData.push({ ...node.data });
    });
    setRowData(currentData);

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find((r) => r.KeyField === row.KeyField)
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !selectedKeys.includes(row.KeyField)
    );

    gridRef.current?.api.clearFocusedCell();
    gridRef.current?.api.deselectAll();

    setTimeout(() => {
      if (gridRef.current?.api) {
        gridRef.current?.api.clearFocusedCell();
        gridRef.current?.api.deselectAll();
        (document.activeElement as HTMLElement)?.blur();
        handleRowFocusAfterDelete(firstSelectedIndex, gridRef.current.api);
      } else {
        console.warn("Grid API null error.");
      }
    }, 0);
  };

  const deleteRowByIndex = (rowIndex: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const node = gridRef.current.api.getDisplayedRowAtIndex(rowIndex);

    if (!node) {
      console.warn(`Row at index ${rowIndex} not found.`);
      return;
    }

    const rowDataToDelete = node.data;

    gridRef.current.api.applyTransaction({ remove: [rowDataToDelete] });

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => row.KeyField !== rowDataToDelete.KeyField)
    );

    // 변경된 행 관리
    if (
      !modifiedRows.current.added.find(
        (addedRow) => addedRow.KeyField === rowDataToDelete.KeyField
      )
    ) {
      modifiedRows.current.deleted.push(rowDataToDelete);
    }

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => row.KeyField !== rowDataToDelete.KeyField
    );
    console.log("Deleted row:", rowDataToDelete);

    // 삭제 후 포커스 조정
    setTimeout(() => {
      if (gridRef.current?.api) {
        handleRowFocusAfterDelete(rowIndex, gridRef.current.api);
      } else {
        console.warn("api null error.");
      }
    }, 0);
  };

  const deleteAllRows = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }
    console.warn("현재 렌더링된 노드 가져오기.");

    // 현재 렌더링된 노드 가져오기
    const renderedNodes = gridRef.current.api.getRenderedNodes();

    if (!renderedNodes || renderedNodes.length === 0) {
      console.warn("No visible rows to delete.");
      return;
    }

    const visibleKeys = renderedNodes.map((node) => node.data.KeyField);
    console.log("Deleting visible rows with KeyField:", visibleKeys);

    const rowsToDelete = rowData.filter((row) =>
      visibleKeys.includes(row.KeyField)
    );

    const rowsToRemove = renderedNodes.map((node) => node.data); // 데이터만 추출
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => !visibleKeys.includes(row.KeyField))
    );

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find(
          (addedRow) => addedRow.KeyField === row.KeyField
        )
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    // // Footer 재계산 추가

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !visibleKeys.includes(row.KeyField)
    );

    console.log("Deleted visible rows:", rowsToDelete);
  };

  //#endregion

  // 포커스된 행 변경 처리
  const handleCellFocused = (event: CellFocusedEvent) => {
    const focusedRowIndex = event.rowIndex;
    let focusedRowData: any = null;

    if (focusedRowIndex === null) {
      return;
    }

    if (focusedRowIndex != null) {
      focusedRowData =
        gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex)?.data ||
        null;
    }

    // 현재 선택된 행 가져오기
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    const selectedRowData = selectedRows.length > 0 ? selectedRows[0] : null;

    if (focusedRowData != selectedRowData) {
      gridRef.current?.api.deselectAll(); // 기존 선택 초기화

      const focusedRowNode =
        gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);
      focusedRowNode?.setSelected(true); // 새 행 선택
    }

    // 포커스된 행이 변경된 경우만 처리
    if (
      focusedRowData !== previousFocusedRowRef.current &&
      onFocusedRowChanged
    ) {
      previousFocusedRowRef.current = focusedRowData; // 상태 업데이트
      onFocusedRowChanged(focusedRowData); // 외부로 이벤트 알림
    }

    // sheet 에 붙여 넣는 첫 시작 위치 저장
    const colId =
      event.column && typeof event.column !== "string"
        ? event.column.getColId()
        : (event.column ?? null);

    if (editCons != null && editCons[colId ? colId : ""] != null) {
      let condata: string = editCons[colId ? colId : ""];

      if (
        Object.hasOwn(focusedRowData, "state") &&
        focusedRowData.state === "insert"
      )
        condata = condata.replace("new", "false");
      else condata = condata.replace("new", "true");

      Object.keys(focusedRowData).forEach((key) => {
        condata = condata.replace(key, focusedRowData[key]);
      });
      const matchingColDef = resolvedColumnDefs.find(
        (colDef) => colDef.field === colId
      );

      const updatedColumnDefs = {
        ...matchingColDef,
        editable: new Function(`return !${condata};`)(),
      } as CustomColDef; // 명시적으로 ColDef 단언

      setResolvedColumnDefs((prevDefs) => {
        const next = prevDefs.map((def) =>
          def.field === colId ? { ...def, ...updatedColumnDefs } : def
        );
        return next as ColDef<any, any>[]; // ← 타입 강제 단언
      });
    }
    // 컬럼 상태 가져오기
  };

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    if (rowSelectionMode === "single") {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else if (rowSelectionMode === "multiple") {
      return {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else {
      return "single"; // 기본값
    }
  }, [rowSelectionMode]);

  // 키보드 입력 처리
  const handleCellKeyDown = async (event: CellKeyDownEvent) => {
    const gridEvent = event.event;

    if (!gridEvent || !(gridEvent instanceof KeyboardEvent)) return;

    const { api, node, column } = event;
    const key = gridEvent.key;

    if (!node || !column || node.rowIndex === null) {
      return;
    }

    console.log("KeyDown Event Triggered:", key);

    // 숫자 및 달력 필드에서 방향키 동작 방지
    const columnDef = column.getColDef() as CustomColDef; // 확장된 타입 사용

    const isNumberOrDateField =
      columnDef?.type === "SnNumberBox" || columnDef?.type === "SnDateBox";

    if (isNumberOrDateField && (key === "ArrowUp" || key === "ArrowDown")) {
      gridEvent.preventDefault(); // 기본 동작(숫자/날짜 변경) 차단
    }

    // 방향키 처리
    if (key === "ArrowUp" || key === "ArrowDown") {
      const isEditing = api.getEditingCells().length > 0;

      if (isEditing) {
        console.log(`Exiting edit mode for key: ${key}`);
        api.stopEditing(); // 에디트 모드 종료

        // 방향키에 따른 포커스 이동
        const targetRowIndex =
          key === "ArrowUp" ? node.rowIndex - 1 : node.rowIndex + 1;

        // 유효한 행인지 확인
        if (
          targetRowIndex >= 0 &&
          targetRowIndex < api.getDisplayedRowCount()
        ) {
          api.setFocusedCell(targetRowIndex, column.getColId()); // 동일한 컬럼에 포커스
          api.ensureIndexVisible(targetRowIndex); // 포커스된 행 가시화
        }
      }

      return; // 방향키 처리는 여기서 종료
    }

    // 기존 방향키 로직 유지
    if (key === "ArrowLeft") {
      const columns = api.getColumns?.(); // getColumns 호출
      if (!columns || columns.length === 0) return; // null 및 빈 배열 처리

      const currentColumnIndex = columns.findIndex(
        (col) => col.getColId() === column.getColId()
      );

      if (currentColumnIndex > 0) {
        const previousColumn = columns[currentColumnIndex - 1];
        if (previousColumn?.getColId() === "indicatorColumn") {
          console.log("ArrowLeft blocked: Cannot move to indicator column.");
          gridEvent.preventDefault();
        }
      }
    }
    // 2025.01.31 cellvaluechange에서 keydown으로 변경
    if (key === "Enter" || key === "Tab") {
      const selectedSingleSetting = singlepopupsRef.current.filter(
        (popup: any) => popup.cd_column === column.getColId()
      );
      const selectedMultiSetting = multipopupsRef.current.filter(
        (popup: any) => popup.cd_column === column.getColId()
      );

      if (selectedSingleSetting.length > 0) {
        if (selectedSingleSetting[0].cd_popup) {
          const popupData = getSearchData(
            selectedSingleSetting[0].cd_popup,
            ""
          );
          const modalData = {
            title: popupData.title,
            code: selectedSingleSetting[0].cd_popup,
            formData: popupData.selectedData,
          };
          const popupSetting = PopupSettings[modalData.code];
          const { columns } = popupSetting;

          const columnField = column.getColId();
          const newValue = event.data[columnField];
          const keyValueObject = {
            [columnField]: newValue, // 컬럼 이름을 키로 사용
            ["str_search"]: newValue,
          };
          const rtn: any = await getInitPopUp(
            selectedSingleSetting[0].cd_popup,
            {}
          );
          const mergedData = { ...rtn.formData, ...keyValueObject };
          const params = {
            id: selectedSingleSetting[0].cd_popup.toLowerCase() + "_01",
            params: mergedData,
          };
          const rowData: any = await fnGetDataSet(params);
          const data = rowData[0];
          if (data && data.length == 1) {
            if (event.node.id !== undefined) {
              const rowNode = event.node; // 현재 행 가져오기
              if (rowNode && rowNode.data) {
                columns.forEach((column: any) => {
                  rowNode.setDataValue(column, data[0][column]); // 셀 값 변경 및 UI 업데이트
                });
                event.api.refreshCells({
                  rowNodes: [rowNode],
                  force: true,
                });
              }
            }
          } else {
            const singlePopup = (
              <SinglePopup
                code={selectedSingleSetting[0].cd_popup}
                selectedData={popupData.selectedData}
              />
            );
            const result = await showPopupModal({
              title: popupData.title,
              children: singlePopup,
            });
            if (result) {
              const resultArr = result[0];
              if (event.node.id !== undefined) {
                const rowNode = event.node; // 현재 행 가져오기
                if (rowNode && rowNode.data) {
                  columns.forEach((column: any) => {
                    rowNode.setDataValue(column, resultArr[0][column]); // 셀 값 변경 및 UI 업데이트
                  });
                  event.api.refreshCells({
                    rowNodes: [rowNode],
                    force: true,
                  });
                }
              }
            }
          }
        }
      } else if (selectedMultiSetting.length > 0) {
        if (selectedMultiSetting[0].cd_popup) {
          const popupData = getSearchData(selectedMultiSetting[0].cd_popup, "");
          const modalData = {
            title: popupData.title,
            code: selectedMultiSetting[0].cd_popup,
            formData: popupData.selectedData,
          };
          const popupSetting = PopupSettings[modalData.code];
          const { columns } = popupSetting;
          const columnField = column.getColId();
          const newValue = event.data[columnField];
          const keyValueObject = {
            [columnField]: newValue, // 컬럼 이름을 키로 사용
            ["str_search"]: newValue,
          };
          const rtn: any = await getInitPopUp(
            selectedMultiSetting[0].cd_popup,
            {}
          );
          const mergedData = { ...rtn.formData, ...keyValueObject };
          const params = {
            id: selectedMultiSetting[0].cd_popup.toLowerCase() + "_01",
            params: mergedData,
          };
          const rowData: any = await fnGetDataSet(params);
          const data = rowData[0];
          if (data && data.length == 1) {
            if (event.node.id !== undefined) {
              const rowNode = event.api.getRowNode(event.node.id); // 특정 행의 노드 찾기
              if (rowNode && rowNode.data) {
                columns.forEach((column: any) => {
                  rowNode.setDataValue(column, data[0][column]); // 셀 값 변경 및 UI 업데이트
                });
                event.api.refreshCells({
                  rowNodes: [rowNode],
                  force: true,
                });
              }
            }
          } else {
            const multiPopup = (
              <MultiPopup
                code={selectedMultiSetting[0].cd_popup}
                selectedData={popupData.selectedData}
              />
            );
            const result = await showPopupModal({
              title: popupData.title,
              children: multiPopup,
            });
            if (result) {
              if (event.node.id !== undefined) {
                const rowNode = event.api.getRowNode(event.node.id);
                if (rowNode && rowNode.data) {
                  const newRows: any[] = [];
                  result.forEach((selectedItem: any) => {
                    if (selectedItem === result[0]) {
                      columns.forEach((col: string) => {
                        event.data[col] = selectedItem[col];
                        event.api.refreshCells({
                          rowNodes: [event.node],
                          force: true,
                        });
                      });
                    } else {
                      const newRow: Record<string, any> = {};
                      columns.forEach((col: string) => {
                        newRow[col] = selectedItem[col];
                      });
                      newRows.push(newRow);
                    }
                  });
                  if (newRows.length > 0) {
                    newRows.forEach((row) => {
                      addRow(row);
                      const addedRow = row;
                    });
                  }
                }
              }
            }
          }
        }
      }
    }
  };

  //#region 2025.01.14 그리드 팝업관련 추가
  const handleCellDoubleClick = async (params: any) => {
    const column = params.colDef;
    const columnfield = column.field;
    const columndisplay = column.cd_displaycolumn;
    const validSinglepopups = Array.isArray(singlepopups) ? singlepopups : [];
    const selectedSingleSetting = singlepopups.filter(
      (popup: any) => popup.cd_column === column.field
    );
    const selectedMultiSetting = multipopups.filter(
      (popup: any) => popup.cd_column === column.field
    );
    if (selectedSingleSetting.length > 0) {
      if (selectedSingleSetting[0].cd_popup) {
        params.api.stopEditing();
        const popupData = getSearchData(selectedSingleSetting[0].cd_popup, "");
        const modalData = {
          title: popupData.title,
          code: selectedSingleSetting[0].cd_popup,
          formData: popupData.selectedData,
        };
        const singlePopup = (
          <SinglePopup
            code={selectedSingleSetting[0].cd_popup}
            selectedData={popupData.selectedData}
          />
        );
        const result = await showPopupModal({
          title: popupData.title,
          children: singlePopup,
        });
        const popupSetting = PopupSettings[modalData.code];
        const { columns } = popupSetting;
        if (result) {
          const resultArr = result[0];
          const transformedData = {
            code: resultArr[columns[0]], // 예: resultArr['cd_biz']
            name: resultArr[columns[1]], // 예: resultArr['nm_wh']
            ...resultArr, // 나머지 값을 그대로 추가
          };
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id); // 특정 행의 노드 찾기
            if (rowNode) {
              if (rowNode.data) {
                // 임시방편
                if (columnfield === "cd_wh_ref") {
                  rowNode.setDataValue("cd_wh_ref", transformedData.code);
                  rowNode.setDataValue("nm_wh_ref", transformedData.name);
                } else {
                  columns.forEach((column: any) => {
                    rowNode.setDataValue(column, resultArr[column]);
                  });
                }
              }
            }
          }
        }
      }
    } else if (selectedMultiSetting.length > 0) {
      if (selectedMultiSetting[0].cd_popup) {
        params.api.stopEditing();
        const popupData = getSearchData(selectedMultiSetting[0].cd_popup, "");
        const modalData = {
          title: popupData.title,
          code: selectedMultiSetting[0].cd_popup,
          formData: popupData.selectedData,
        };
        const multiPopup = (
          <MultiPopup
            code={selectedMultiSetting[0].cd_popup}
            selectedData={popupData.selectedData}
          />
        );
        const result = await showPopupModal({
          title: popupData.title,
          children: multiPopup,
        });
        const popupSetting = PopupSettings[modalData.code];
        const { columns } = popupSetting;
        if (result && result.length > 0) {
          if (params.node.id !== undefined) {
            const rowNode = params.api.getRowNode(params.node.id);
            if (rowNode && rowNode.data) {
              const newRows: any[] = [];
              result.forEach((selectedItem: any) => {
                if (selectedItem === result[0]) {
                  columns.forEach((col: string) => {
                    params.data[col] = selectedItem[col];
                    params.api.refreshCells({
                      rowNodes: [params.node],
                      force: true,
                    });
                  });
                } else {
                  const newRow: Record<string, any> = {};
                  columns.forEach((col: string) => {
                    newRow[col] = selectedItem[col];
                  });
                  newRows.push(newRow);
                }
              });
              if (newRows.length > 0) {
                newRows.forEach((row) => {
                  addRow(row);
                  const addedRow = row;
                });
              }
            }
          }
        }
      }
    }
    if (column.yn_drilldown === "Y") {
      alert("드릴다운");
      // const data = {
      //   cd_menu : 'SN_SAL_SAO_REG_001',
      //   cd_menu_parent : 'SN_SAL_SAO_MGM_001',
      //   cd_module : 'SAL',
      //   fg_delete : 'Y',
      //   fg_excel : 'Y',
      //   fg_insert : 'Y',
      //   fg_menu : 'REG',
      //   fg_print : 'Y',
      //   fg_select : 'Y',
      //   fg_update : 'Y',
      //   nm_file : 'SN_SAL_SAO_REG_001',
      //   nm_menu : '수주등록',
      //   nm_menu_origin : '수주등록',
      //   no_level : 3,
      //   no_pos : '002003001',
      //   no_seq : 1,
      //   // path_arr : (3) ['SN_SAL', 'SN_SAL_SAO_MGM_001', 'SN_SAL_SAO_REG_001'],
      //   yn_end : 'Y'
      // };

      // const { nm_menu, nm_file, cd_menu, cd_module } = data;

      // const filePath = `./${data.cd_module}/${data.nm_file}/${data.nm_file}`.toUpperCase();

      // handleMenuClick('SN_SAL_SAO_REG_001', '수주등록', 'SAL', './SAL/SN_SAL_SAO_REG_001/SN_SAL_SAO_REG_001', data, '영업관리 > 수주관리 > 수주등록');
    }
  };

  const singlepopupsRef = useRef(singlepopups);
  const multipopupsRef = useRef(multipopups);

  useEffect(() => {
    singlepopupsRef.current = singlepopups;
  }, [singlepopups]);
  useEffect(() => {
    multipopupsRef.current = multipopups;
  }, [multipopups]);

  //#endregion

  const selectAndFocusRow = (index: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 행 인덱스를 확인하여 범위 내인지 체크
    const totalRowCount = api.getDisplayedRowCount();
    if (index < 0 || index >= totalRowCount) {
      console.warn(`Index ${index} 오류. Total rows: ${totalRowCount}`);
      return;
    }

    // 해당 인덱스의 행 노드를 가져오기
    const rowNode = api.getDisplayedRowAtIndex(index);
    if (rowNode) {
      // 행 선택
      rowNode.setSelected(true);

      // 해당 행이 화면에 보이도록 스크롤
      api.ensureIndexVisible(index);

      const nonCheckboxCols = resolvedColumnDefs.filter(
        (col) => !col.checkboxSelection
      );

      // 수정 가능한 컬럼 또는 기본 컬럼 선택
      const firstFocusableCol =
        nonCheckboxCols.find((col) => col.editable)?.field || // 수정 가능한 컬럼
        nonCheckboxCols[0]?.field; // 기본 컬럼 (체크박스 제외 후 맨 앞 컬럼)

      if (firstFocusableCol) {
        api.setFocusedCell(index, firstFocusableCol);
      }
    } else {
      console.warn(`Row node at index ${index} is not found.`);
    }
  };

  // 필더링된 행만 전체 선택하는 함수
  const selectAllFiltered = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 2. 필터링된 rows 전체 선택
    api.selectAllFiltered();
  };

  const handleInternalonRowClicked = (event: any) => {
    if (onRowClicked) {
      onRowClicked(event); // 외부 핸들러 호출
    }
  };

  const handleInternalCellValueChanged = async (event: any) => {
    if (onBeforeEdit && onBeforeEdit(event.data, event.colDef) === false) {
      console.log("Edit cancelled by onBeforeEdit");
      return;
    }

    const currentFocusedCell = gridRef.current?.api.getFocusedCell(); // 현재 포커스된 셀 저장

    if (onCellValueChanged) {
      onCellValueChanged(event); // 외부 핸들러 호출
    }

    const updatedRow = { ...event.data };
    gridRef.current?.api.applyTransaction({ update: [updatedRow] });

    // 변경된 셀 강제 렌더링
    gridRef.current?.api.refreshCells({
      rowNodes: [event.node],
      force: true, // 강제 렌더링
    });

    // setRowData((prev) =>
    //   prev.map((row) =>
    //     row.KeyField === updatedRow.KeyField ? updatedRow : row
    //   )
    // );

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === event.data.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = event.data;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === event.data.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = event.data;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(event.data);
      }
    }

    // // 데이터 변경 후 포커스 복원
    // setTimeout(() => {
    //   if (currentFocusedCell) {
    //     gridRef.current?.api.setFocusedCell(
    //       currentFocusedCell.rowIndex,
    //       currentFocusedCell.column.getColId()
    //     );
    //   }
    // }, 0); // 데이터 렌더링 완료 후 포커스 복원

    // // 2. 합계 재계산

    computeFooterSummary();

    // // **다음 수정 가능한 셀로 포커스 이동 또는 행 추가**
    // setTimeout(() => {
    //   if (gridRef.current?.api && event.node) {
    //     const currentColId = event.column.colId;
    //     const currentRowIndex = event.node.rowIndex;

    //     // 현재 열의 인덱스
    //     const colIndex = resolvedColumnDefs.findIndex(
    //       (col) => col.field === currentColId
    //     );

    //     // 마지막 컬럼 여부 확인
    //     const isLastCol =
    //       resolvedColumnDefs.slice(colIndex + 1).find((col) => col.editable) ===
    //       undefined;

    //     if (isLastCol) {
    //       // 마지막 컬럼인 경우
    //       const isLastRow = currentRowIndex === rowData.length - 1;

    //       if (isLastRow) {
    //         // 마지막 행의 마지막 컬럼: 행 추가
    //         if (props.enableAddRow) {
    //           addRow();
    //         }
    //       } else {
    //         // 마지막 컬럼이지만 마지막 행이 아님: 다음 행의 첫 번째 수정 가능한 셀로 이동
    //         const firstEditableCol = resolvedColumnDefs.find(
    //           (col) => col.editable
    //         )?.field;
    //         if (firstEditableCol) {
    //           //gridRef.current.api.ensureIndexVisible(currentRowIndex + 1); // 새 행이 보이도록 스크롤
    //           gridRef.current.api.setFocusedCell(
    //             currentRowIndex + 1,
    //             firstEditableCol
    //           ); // 포커스를 명시적으로 설정
    //           // gridRef.current.api.startEditingCell({
    //           //   rowIndex: currentRowIndex + 1, // 다음 행
    //           //   colKey: firstEditableCol, // 첫 번째 수정 가능한 컬럼
    //           // });
    //         }
    //       }
    //     } else {
    //       // 같은 행의 다음 수정 가능한 셀로 이동
    //       const nextColId = resolvedColumnDefs
    //         .slice(colIndex + 1)
    //         .find((col) => col.editable)?.field;
    //       if (nextColId) {
    //         gridRef.current.api.ensureIndexVisible(currentRowIndex); // 새 행이 보이도록 스크롤
    //         gridRef.current.api.setFocusedCell(currentRowIndex, nextColId); // 포커스를 명시적으로 설정
    //         // gridRef.current.api.startEditingCell({
    //         //   rowIndex: currentRowIndex,
    //         //   colKey: nextColId,
    //         // });
    //       }
    //     }
    //   }
    // }, 0);
  };

  //#endregion

  //#region getChange 변경된 데이터 가져오기
  // 2025.01.08 필수값 체크로직 추가
  const getChange = () => {
    return {
      modifiedRowcount:
        modifiedRows.current.added.length +
        modifiedRows.current.updated.length +
        modifiedRows.current.deleted.length,
      insert: modifiedRows.current.added,
      update: modifiedRows.current.updated,
      delete: modifiedRows.current.deleted,
    };
  };

  const requiredValuesCheck = () => {
    try {
      modifiedRows.current.added.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은 필수값입니다");
            throw "finish";
          }
        });
      });
      modifiedRows.current.updated.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은 필수값입니다");
            throw "finish";
          }
        });
      });
      return true;
    } catch (Exception) {
      if (Exception !== "finish") throw Exception;
      else return false;
    }
  };

  //#endregion

  //#region resetUpdate 저장 후 상태 초기화
  const resetUpdate = () => {
    modifiedRows.current = { added: [], updated: [], deleted: [] };
  };

  //#endregion

  //#region 하단 차트 관련 내용

  const chartContainerRef = useRef<HTMLDivElement>(null); // 독립 차트 컨테이너 참조
  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API

  const handleGridReady = (params: any) => {
    const { api, columnApi } = params;

    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;

    setIsGridReady(true);

    if (onGridReady) {
      onGridReady(params); // 외부 콜백 호출
    }

    console.log("Grid API:", api);
    console.log("Column API:", columnApi);

    // 데이터가 렌더링된 후 기본 차트 생성
    if (
      enableExternalChart &&
      chartColumns.length > 0 &&
      chartContainerRef.current
    ) {
      try {
        api.createRangeChart({
          cellRange: {
            columns: chartColumns,
          },
          chartType: "groupedColumn", // 차트 유형
          chartContainer: chartContainerRef.current, // 독립 차트 컨테이너
        });
        console.log("Chart successfully created!");
      } catch (error) {
        console.error("Error creating chart:", error);
      }
    }
  };

  //#endregion

  //#region 필터 관련 내용

  // 필터를 적용하는 메서드
  const applyFilters = (filterConditions: { [key: string]: any }) => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결
      gridRef.current.api.showLoadingOverlay();

      // 2. 필터링 작업 수행
      const filterModel: { [key: string]: any } = {};

      // filterConditions에 따라 필터 모델 생성

      Object.keys(filterConditions).forEach((field) => {
        const value = filterConditions[field];

        filterModel[field] =
          value === "" || value === undefined
            ? { type: "blank" } // 빈 값은 "blank"로 필터링
            : { type: "equals", filter: value }; // 특정 값은 "equals"로 필터링
      });

      console.log("Applying filters:", filterModel);

      gridRef.current.api.setFilterModel(filterModel);

      // 3. 필터 상태 반영
      gridRef.current.api.onFilterChanged();

      // const updatedRowData =
      //   gridRef.current?.api.getRenderedNodes().map((node) => node.data) || [];
      // calculateFooter(updatedRowData); // 변경된 데이터를 기반으로 합계 재계산

      computeFooterSummary();

      // 4. 작업 종료: 화면 활성화 (작업 완료 후 약간의 지연 추가)
    }
  };

  // 필터를 초기화하는 메서드
  const resetFilters = () => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결

      // 2. 필터 초기화
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.onFilterChanged();

      // 3. 작업 종료: 화면 활성화
    }
  };

  //#endregion

  //#region Indicator 관련 내용
  const columnDefsWithRowNumber = [
    {
      headerName: "No.", // 순번 컬럼
      valueGetter: (params: { node: { rowPinned: any; rowIndex: number } }) => {
        if (params.node.rowPinned) {
          const visibleRowCount =
            gridRef.current?.api.getDisplayedRowCount() || 0;
          return visibleRowCount;
        }
        return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
      },
      colId: "fixIndicator",
      width: 70, // 너비
      pinned: "left", // 왼쪽 고정
      cellClass: "row-number-cell", // 스타일 클래스
      suppressMovable: true, // 이동 불가
      sortable: false, // 정렬 불가
      filter: false, // 필터링 불가
    },
    ...resolvedColumnDefs, // 기존 컬럼 정의
  ];

  //#endregion

  //#region shareData 데이터 업데이트
  const sharedCellValueChanged = (data: any) => {
    const Change = { ...data };
    setRowData((prev) =>
      prev.map((row) => (row.KeyField === Change.KeyField ? Change : row))
    );

    gridRef.current?.api.applyTransaction({ update: [Change] });

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === Change.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = Change;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === Change.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = Change;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(Change);
      }
    }

    // // 2. 합계 재계산

    computeFooterSummary();
  };

  const suppressEventDispatchRef = useRef(false); // 이벤트 억제 상태

  // 이벤트 억제 활성화
  const suppressEventDispatch = () => {
    suppressEventDispatchRef.current = true;
  };

  // 이벤트 억제 비활성화
  const allowEventDispatch = () => {
    suppressEventDispatchRef.current = false;
  };

  // 현재 이벤트 디스패치 가능 여부 확인
  const shouldDispatchEvent = () => !suppressEventDispatchRef.current;

  //#endregion

  // column 존재 여부 체크하여 setDataValue
  let popupData: boolean;
  const setValue = (rowNode: RowNode, column: string, value: any) => {
    const gridApi = gridRef.current?.api;
    popupData = false;

    if (!gridApi || !rowNode) return;

    const colDef = gridApi.getColumnDef(column);
    if (!colDef) {
      console.warn(`Column '${column}' does not exist.`);
      return;
    }
    const oldValue = rowNode.data[column];

    // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
    if (oldValue !== value || oldValue == null) {
      // 값 업데이트
      rowNode.setDataValue(column, value);
    }
  };

  // 드릴다운으로 할 때 column 존재 여부 체크하여 setDataValue
  const setValueDrill = (rowNode: RowNode, column: string, value: any) => {
    const gridApi = gridRef.current?.api;

    if (!gridApi || !rowNode) return;

    const colDef = gridApi.getColumnDef(column);
    if (!colDef) {
      console.warn(`Column '${column}' does not exist.`);
      return;
    }
    const oldValue = rowNode.data[column];

    // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
    if (oldValue !== value || oldValue == null) {
      // 값 업데이트
      rowNode.setDataValue(column, value);
    }
  };

  // RowData 업데이트 후 포커스를 재설정하는 함수
  const handleRowDataUpdated = () => {
    if (gridRef.current?.api) {
      const lastSelectedNode = gridRef.current.api.getSelectedNodes()[0]; // 마지막 선택된 행 노드
      if (lastSelectedNode) {
        const rowIndex = lastSelectedNode.rowIndex ?? 0; // null 또는 undefined 시 0으로 설정
        // columnDefs가 존재할 때만 find 실행
        const firstEditableCol = columnDefs?.find(
          (col: ColDef) => col.editable
        )?.field; // 첫 번째 수정 가능한 컬럼

        if (firstEditableCol) {
          // 포커스 재설정
          gridRef.current.api.setFocusedCell(rowIndex, firstEditableCol);
        }
      }
    }
  };

  // 메뉴마다 우클릭했을 때 popmenu가 그리드별로 다르기에 seq를 추가함. 메뉴에서 그리드에 추가해주면됨
  // sys_popmenu 테이블에 no_seq_dw임
  const { seq } = props;
  const menuItemsRef = useRef<
    Array<Array<{ name: string; action: () => void }>>
  >([]);
  // 우클릭 했을 때 커스텀을 하려했는데 비동기작업이 안된다고함.
  // 그래서 메뉴페이지 열때 값을 가져와 만들어 두고 그걸 사용하려고 추가했음.
  // 더 좋은 방법이 있다면 수정하셔도 되고 말해주셔도 되고 좋습니다.
  useEffect(() => {
    const fetchMenuItems = async () => {
      const fileName = props.menuInfo?.cd_menu;
      if (!fileName) {
        console.log("fileName is missing or empty, skipping fetchMenuItems");
        return;
      }
      const seqs = Array.isArray(props.seq)
        ? props.seq.filter((seq) => seq !== undefined)
        : [props.seq].filter((seq) => seq !== undefined);
      let allMenuItems: any[] = [];
      try {
        const results = await Promise.all(
          seqs.map(async (seq: number, index: number) => {
            const params = {
              id: "com_CommonMapper_09",
              params: {
                cd_menu: fileName,
                cd_firm: userInfo.cd_firm,
                no_seq_dw: seq,
              },
            };
            const data: any = await fnGetDataSet(params);
            if (Array.isArray(data) && data.length > 0) {
              const menuItems: { name: string; action: () => void }[] = [];
              for (let i = 0; i < data[0].length; i++) {
                const item = data[0][i];
                const menuName: string =
                  item?.nm_popmenu || item?.cd_popmenu || "";
                menuItems.push({
                  name: menuName,
                  action: () => {
                    console.log(`${menuName} clicked`);
                  },
                });
              }
              menuItemsRef.current[seq] = menuItems;
              return menuItems;
            }
            return [];
          })
        );
        allMenuItems = allMenuItems.concat(...results);
        menuItemsRef.current = allMenuItems;
      } catch (error) {
        console.error("Error fetching context menu data:", error);
      }
    };
    if (props.menuInfo?.cd_menu) {
      fetchMenuItems();
    }
  }, []);

  const gridOptionsExt = {
    ...props.gridOptions,
    suppressAggFuncInHeader: true, // 그룹화하고나면 컬럼에 sum 표시 제거
    suppressScrollOnNewData: true, // 데이터 업데이트 시 스크롤 방지
    suppressColumnVirtualisation: true, // 가상화 비활성화
    rowDragManaged: true, //  강제 활성화
    enableClipboard: true, // 클립보드 기능 활성화
    suppressClipboardPaste: false, // 붙여넣기 금지 설정 해제
    enableBrowserTooltips: true, // 툴팁 활성화
    processCellForClipboard,
    processCellForExport,

    // 우클릭 메뉴 만들어 사용하기인데 디폴드메뉴가 있으면 합쳐서 보여주는거임.
    getContextMenuItems: (params: any) => {
      const today = new Date();
      if (props.getContextMenuItems) {
        return props.getContextMenuItems(params);
      }

      const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");
      const formattedTime = today.toTimeString().slice(0, 8).replace(/:/g, "");

      const defaultItems = Array.isArray(params.defaultItems)
        ? params.defaultItems
        : [];
      if (defaultItems.length === 0) {
        return [];
      }
      console.log("menuInfo", menuInfo);
      if (defaultItems.length > 6) {
        defaultItems[6] = {
          name: "내보내기",
          subMenu: [
            {
              name: "CSV로 내보내기",
              action: () =>
                gridRef.current!.api.exportDataAsExcel({
                  fileName: `${menuInfo?.nm_menu}[${formattedDate}${formattedTime}].csv`,
                  sheetName: "다운로드",
                }),
            },
            {
              name: "Excel로 내보내기",
              action: () =>
                gridRef.current!.api.exportDataAsExcel({
                  fileName: `${menuInfo?.nm_menu}[${formattedDate}${formattedTime}].xlsx`,
                  sheetName: "다운로드",
                }),
            },
          ],
        };
      }
      const customMenuItems = menuItemsRef.current.map((item: any) => {
        if (item.name === "-") {
          return "separator";
        }
        // 일반 메뉴 항목
        return {
          name: item.name,
          action: item.action,
        };
      });
      return [...defaultItems, "separator", ...customMenuItems];
    },
  };
  // 2025.01.15 우클릭 크기 그리드 고정제외
  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector("body");
  }, []);

  const handleOnRowDoubleClicked = (event: any) => {
    console.log("AgGridReact event", event);
    if (onRowDoubleClicked) {
      onRowDoubleClicked(event);
    }
  };

  const handleonCellDoubleClicked = (event: any) => {
    console.log("AgGridReact vent", event);
    if (onCellDoubleClicked) {
      onCellDoubleClicked(event);
    }
  };

  // 드래그 후 로우 순번 재정의 하는 부분
  const handleRowDragEnd = () => {
    if (gridRef.current?.api) {
      // const updatedData = gridRef.current.api.getDisplayedRowAtIndex(0)!.data;
      const allData: RowData[] = [];

      gridRef.current.api.forEachNode((node, index) => {
        if (node.data) {
          allData.push({ ...node.data, no_seq_sort: index + 1 });
        }
      });

      setRowData(allData);
    }
  };

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [filterText, setFilterText] = useState("");
  const currentIndexRef = useRef(0);
  const filterTextRef = useRef("");

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const focusedCell = gridRef.current?.api.getFocusedCell();
    if (focusedCell) {
      if (event.ctrlKey && event.key === "f") {
        event.preventDefault();
        setIsFilterVisible(true);
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const onFilterTextBoxChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      filterTextRef.current = e.target.value;
    },
    []
  );

  const findCellAndFocus = useCallback(() => {
    const gridApi = gridRef.current?.api;
    const columns = gridApi?.getColumns();
    if (!filterTextRef.current || !gridApi || !columns) return;

    let found = false;
    let startIndex = currentIndexRef.current;

    gridApi.forEachNodeAfterFilterAndSort((rowNode, index) => {
      if (found || index < startIndex) return;

      const rowIndex = rowNode.rowIndex;
      const rowData = rowNode.data;

      for (let col of columns) {
        const colId = col.getColId();
        const cellValue = rowData[colId];

        if (cellValue?.toString().includes(filterTextRef.current)) {
          if (rowIndex !== null) {
            gridApi.setFocusedCell(rowIndex, colId);
            gridApi.ensureIndexVisible(rowIndex);
          }
          currentIndexRef.current = index + 1;
          found = true;
          break;
        }
      }
    });

    if (!found && startIndex !== 0) {
      currentIndexRef.current = 0;
      findCellAndFocus();
    }
  }, [filterText]);

  const closeFilterBox = () => {
    setFilterText("");
    setIsFilterVisible(false);
  };

  const handleInternalSelectionChanged = (
    gridRef: any,
    onSelectionChanged?: (selectedRows: any[]) => void
  ) => {
    if (gridRef?.current?.api && onSelectionChanged) {
      const selectedRows = gridRef.current.api.getSelectedRows(); //  선택된 행 가져오기
      onSelectionChanged(selectedRows); //  부모 컴포넌트로 전달
    }
  };

  /**
   * 붙여넣는 글 정리 — 앞뒤 공백을 걷고, 날짜 칸이면 yyyyMMdd 로 바꾼다(변환 실패 시 원본 유지).
   * 붙여넣기와 챗봇이 넘긴 표(applyOfferedTable)가 같이 쓴다
   */
  const normalizePastedCells = (
    data: any[][],
    startColIndex: number,
    visibleColumns: ColDef[]
  ): string[][] =>
    data.map((row) =>
      row.map((cell, j) => {
        const text = typeof cell === "string" ? cell.trim() : String(cell);
        if (startColIndex === -1 || text === "") return text;
        const col = visibleColumns[startColIndex + j] as any;
        if (!col?.field) return text;

        const isDateCol =
          col.field.startsWith("dt_") ||
          col.tp_column === "SnDateBox" ||
          col.type === "SnDateBox";
        if (!isDateCol) return text;

        return parseToyyyyMMddSnSheet(text, col.tp_format) ?? text;
      })
    );

  /// 붙여 넣기 부분
  const handlePasteData = (
    params: ProcessDataFromClipboardParams<any, any>
  ): string[][] | null => {
    if (!params.data || params.data.length === 0) {
      return null;
    }

    // 현재 포커스된 셀에서 컬럼 ID 가져오기
    const focusedCell = gridRef.current?.api.getFocusedCell();
    if (!focusedCell) {
      //console.warn(" 선택된 셀이 없음.");
      return null;
    }

    const startRowIndex = focusedCell.rowIndex ?? 0; //  포커스된 행
    const startColId = focusedCell.column.getColId(); // 포커스된 컬럼의 ID

    //  visible: false가 아닌 컬럼만 필터링하여 순서 유지
    const visibleColumns = resolvedColumnDefs.filter(
      (col) => col.field && col.hide !== true
    );

    //  포커스된 컬럼의 index 찾기
    const startColIndex = visibleColumns.findIndex(
      (col) => col.field === startColId
    );

    // 붙여넣기 시 공백 제거 (ltrim/rtrim)
    //  + 날짜 컬럼이면 붙여넣는 즉시 yyyyMMdd로 강제 변환 (변환 실패 시 원본 유지)
    //  이 배열을 ag-grid에 반환하므로 여기서 변환해야 그리드 셀에도 변환값이 들어간다.
    const pastedData = normalizePastedCells(
      params.data,
      startColIndex,
      visibleColumns
    );

    // console.log(` 시작 위치: Row ${startRowIndex}, Column ID: ${startColId}`);

    if (startColIndex === -1) {
      //console.warn(`선택한 컬럼(${startColId})이 유효하지 않음.`);
      return pastedData; // 기존 동작 유지: ag-grid 기본 붙여넣기만 수행
    }

    setRowData((prevRowData) => {
      const updatedRows = [...prevRowData];
      const newRows: RowData[] = [];

      // console.log(` 붙여넣기 시작 컬럼 인덱스: ${startColIndex}`);

      pastedData.forEach((row, pasteIndex) => {
        const targetRowIndex = startRowIndex + pasteIndex; //  붙여넣기 시작 위치부터 채우기
        const existingRow = updatedRows[targetRowIndex]; //  기존 행 가져오기

        const rowData: RowData = updatedRows[targetRowIndex] || {
          KeyField: generateKey(),
        };

        // visibleColumns.forEach((col, colIndex) => {
        //   const targetColIndex = startColIndex + colIndex; //  포커스된 컬럼부터 붙여넣기

        //   if (col.field && targetColIndex < row.length) {
        //     rowData[col.field] = row[targetColIndex]; //  붙여넣기 데이터 적용
        //   }
        // });

        for (
          let j = 0;
          j < row.length && startColIndex + j < visibleColumns.length;
          j++
        ) {
          const col = visibleColumns[startColIndex + j];
          if (col?.field) {
            rowData[col.field] = row[j]; //  붙여넣은 첫 값 -> 시작 컬럼 (날짜는 위에서 이미 변환됨)
          }
        }

        //  기존 행이 있으면 업데이트, 없으면 추가
        if (existingRow) {
          updatedRows[targetRowIndex] = rowData;
        } else {
          updatedRows.push(rowData);
        }

        newRows.push(rowData);
      });

      gridRef.current?.api.applyTransaction({ update: updatedRows });
      return updatedRows;
    });

    //  붙여넣기한 첫 번째 행이 보이도록 스크롤 이동
    setTimeout(() => {
      gridRef.current?.api.refreshCells({ force: true });
      gridRef.current?.api.ensureIndexVisible(startRowIndex);
    }, 50);

    return pastedData;
  };

  const resetGridData = async () => {
    console.log(" 그리드 및 버퍼 초기화 실행");

    //  모든 행을 삭제하기 위해 현재 rowData 가져오기
    const allRows = [...rowData];

    //  버퍼 초기화
    resetUpdate();

    //  빈 행 생성 (최소 100개의 빈 행 유지)
    const emptyRows = Array.from({ length: 20 }, () => ({
      KeyField: generateKey(), // 고유 키 생성
    }));

    // AG Grid의 모든 행 제거
    if (gridRef.current?.api) {
      // applyTransactionAsync 콜백을 초기화 완료 시점으로 사용한다.
      await new Promise<void>((resolve) => {
        gridRef.current!.api.applyTransactionAsync(
          {
            remove: allRows,
            add: [emptyRows],
          },
          () => resolve()
        );
      });
      gridRef.current.api.flushAsyncTransactions?.();
    }

    // //  rowData 상태도 초기화하면서 빈 행 유지
    // setRowData([emptyRows]);

    if (gridRef.current?.api) {
      setRowData(emptyRows);
      setErrorMap(new Map());
      gridRef.current.api.refreshCells({ force: true });
    }

    console.log(" 그리드 및 버퍼 초기화 완료");

    // 챗봇이 맡긴 표가 있으면 이 초기화 뒤에 채운다(엑셀 업로드 팝업, 2026-10-28).
    // 빈 행으로 덮이지 않게 초기화가 끝난 다음에 꺼낸다. 칸이 아직 없으면 칸이 생길 때 채운다
    const offer = takeExcelUploadTable();
    if (offer) {
      offeredTableRef.current = offer;
      applyOfferedTable();
    }
  };

  const offeredTableRef = useRef<ReturnType<typeof takeExcelUploadTable>>(null);
  // 최신 칸 정의 — resetGridData 는 칸을 정하기 전 렌더의 함수로 불려 await 뒤에도 옛 칸(빈 목록)을 본다(리뷰, 2026-10-28)
  const resolvedColumnDefsRef = useRef(resolvedColumnDefs);
  resolvedColumnDefsRef.current = resolvedColumnDefs;

  /** 챗봇이 맡긴 표를 머리글로 칸에 맞춰 넣는다. 넣은 행은 붙여넣은 행과 같게 다뤄진다 */
  const applyOfferedTable = () => {
    const offer = offeredTableRef.current;
    const visibleColumns = resolvedColumnDefsRef.current.filter(
      (col) => col.field && col.hide !== true
    );
    if (!offer || !gridRef.current?.api || visibleColumns.length === 0) return;
    offeredTableRef.current = null;
    const mapped = mapExcelUploadTable(
      offer.table,
      visibleColumns.map((col) => ({
        field: String(col.field),
        header: String(col.headerName ?? ""),
      }))
    );
    const rows: RowData[] = normalizePastedCells(
      mapped.rows,
      0,
      visibleColumns
    ).map((cells) => {
      const row: RowData = { KeyField: generateKey() };
      cells.forEach((value, j) => {
        if (value !== "") row[visibleColumns[j].field as string] = value;
      });
      return row;
    });
    setRowData(rows);
    setErrorMap(new Map());
    offer.done({
      applied: rows.length,
      matched: mapped.matched,
      positional: mapped.positional,
    });
  };

  useEffect(() => {
    applyOfferedTable();
  }, [resolvedColumnDefs]);
  const prevErrorMapRef = useRef(new Map());

  const showLoading = (message?: string) => {
    if (!gridRef.current?.api) return;
    const skipVisualLoading =
      (gridRef.current.api as any)?.__skipVisualLoadingOverlay ?? false;
    if (skipVisualLoading) return;
    if (message) {
      const template = `<span class="ag-overlay-loading-center">${message}</span>`;
      gridRef.current.api.setGridOption?.("overlayLoadingTemplate", template);
    }
    const loadingMessage = (gridRef.current.api as any)?.__loadingMessage;
    if (loadingMessage) {
      const template = `<span class="ag-overlay-loading-center">${loadingMessage}</span>`;
      gridRef.current.api.setGridOption?.("overlayLoadingTemplate", template);
    }
    gridRef.current.api.showLoadingOverlay();
  };

  const hideLoading = () => {
    const keepOverlayUntilCallerDone =
      (gridRef.current?.api as any)?.__keepOverlayUntilCallerDone ?? false;
    if (!keepOverlayUntilCallerDone) {
      gridRef.current?.api?.hideOverlay();
    }
  };

  const setLoadingMessage = (message: string) => {
    if (!gridRef.current?.api) return;
    (gridRef.current.api as any).__loadingMessage = message;
    const template = `<span class="ag-overlay-loading-center">${message}</span>`;
    gridRef.current.api.setGridOption?.("overlayLoadingTemplate", template);
    gridRef.current.api.showLoadingOverlay();
  };

  //  errorMap 변경 시 오류 있는 행 + 오류가 해결된 행만 업데이트
  useEffect(() => {
    if (!gridRef.current?.api) return;

    //  errorMap.keys()를 Set에 직접 사용하지 않고, Array.from()으로 변환
    const rowsToUpdate = new Set([
      ...Array.from(errorMap.keys()),
      ...Array.from(prevErrorMapRef.current.keys()),
    ]);

    //  getRowNode(rowIndex)가 undefined가 될 가능성 방지
    const rowNodesToUpdate: RowNode[] = Array.from(rowsToUpdate)
      .map((rowIndex) => gridRef.current!.api.getRowNode(rowIndex))
      .filter((node): node is RowNode => Boolean(node));

    gridRef.current.api.refreshCells({
      rowNodes: rowNodesToUpdate,
      force: true,
    });

    //  prevErrorMap 업데이트 (다음 비교를 위해)
    prevErrorMapRef.current = new Map(errorMap);
  }, [errorMap]);

  const validateData = async (
    updateValues: boolean = false // ← 기본값 true (기존 동작 유지)
  ): Promise<boolean> => {
    if (!gridRef.current || !gridRef.current.api) {
      return false;
    }

    const suppressLoading =
      (gridRef.current.api as any)?.__suppressLoadingOverlay ?? false;
    const keepOverlayUntilCallerDone =
      (gridRef.current.api as any)?.__keepOverlayUntilCallerDone ?? false;
    if (!suppressLoading) {
      showLoading();
    }

    try {
      const changedOnly =
        (gridRef.current.api as any)?.__validateChangedOnly ?? false;
      const changedRows = new Set<any>();
      const changedKeys = new Set<string>();
      if (changedOnly) {
        const change = getChange();
        (change.insert ?? []).forEach((row: any) => {
          changedRows.add(row);
          if (row?.KeyField) changedKeys.add(String(row.KeyField));
        });
        (change.update ?? []).forEach((row: any) => {
          changedRows.add(row);
          if (row?.KeyField) changedKeys.add(String(row.KeyField));
        });
      }
      let newErrorMap = new Map(); // 검증 시점의 오류만 재구성
      let hasErrors = false; // 오류 여부 확인

      //  엑셀 내부 중복(PK) 검사용 맵: key = compareFields 조합 문자열, value = 최초 행 index
      const duplicateKeyMap = new Map<
        string,
        { rowIndex: number; isChanged: boolean }
      >();
      const makePkKey = (row: any) => {
        if (!compareFields || compareFields.length === 0) return "";
        // PK 필드의 값을 trim 후 '|'로 연결 (null/undefined는 공백으로 처리)
        return compareFields
          .map((f) => (row && row[f] != null ? String(row[f]).trim() : ""))
          .join("|");
      };
      // [ADD] 중복 표시 헬퍼: 해당 행의 PK 필드들에 오류 마킹
      const markDuplicateRow = (rowIndex: number, message: string) => {
        if (rowIndex == null || rowIndex < 0) return;
        const rowErr = newErrorMap.get(rowIndex) || {
          fields: new Set<string>(),
          messages: new Map<string, string>(),
        };
        compareFields.forEach((f) => {
          rowErr.fields.add(f);
          rowErr.messages.set(f, message);
        });
        newErrorMap.set(rowIndex, rowErr);
        hasErrors = true;
      };

      //  버퍼 초기화
      resetUpdate();
      //  cd_clas 데이터 로드 (중복 호출 방지)
      const uniqueCdClas = Array.from(
        new Set(
          memoizedColumnConfig!
            .filter((col) => col.cd_clas && typeof col.cd_clas === "string")
            .map((col) => col.cd_clas as string)
        )
      );

      await Promise.all(
        uniqueCdClas.map(async (cd_clas) => {
          if (!codeDataCacheRef.current.has(cd_clas)) {
            codeDataCacheRef.current.set(
              cd_clas,
              await getCodeData(cd_clas, "")
            );
          }
        })
      );

      // cd_popup 데이터 로드 (중복 호출 방지)
      const uniqueCdPopup = Array.from(
        new Set(
          memoizedColumnConfig!
            .filter((col) => col.cd_popup && typeof col.cd_popup === "string")
            .map((col) => col.cd_popup as string)
        )
      );

      await Promise.all(
        uniqueCdPopup.map(async (cd_popup) => {
          if (!popupDataCacheRef.current.has(cd_popup)) {
            popupDataCacheRef.current.set(
              cd_popup,
              await getCodeData(cd_popup, "")
            );
          }
        })
      );
      const poDtMap = new Map<string, string>();
      // 성능: 입력 판정에서 제외할 시스템/출력 컬럼 목록
      const EXCLUDED_INPUT_FIELDS = new Set([
        "__upload_error_summary",
        "No.",
        "no",
        "__op",
        "__state",
        "KeyField",
      ]);
      // 성능: 실제 입력 컬럼만 1회 계산해 row마다 재계산을 피한다.
      const inputFields = (resolvedColumnDefs as CustomColDef[])
        .map((c) => c.field)
        .filter((f): f is string => !!f && !EXCLUDED_INPUT_FIELDS.has(f));

      // 코드/콤보 값 매칭.
      // 엑셀에서 넘어온 값은 앞뒤 공백, 대소문자, 숫자 변환('001' → 1)으로
      // 코드표와 완전 일치하지 않는 경우가 많아 정규화해서 비교한다.
      const normCode = (v: any) =>
        v == null ? "" : String(v).trim().toUpperCase();
      const findCodeItem = (list: any[], value: any) => {
        const target = normCode(value);
        if (target === "") return undefined;
        const exact = (list ?? []).find(
          (item: any) =>
            normCode(item?.code) === target || normCode(item?.name) === target
        );
        if (exact) return exact;

        // '001' 과 '1' 처럼 앞자리 0 차이만 있는 숫자형 코드까지 허용
        if (!/^\d+$/.test(target)) return undefined;
        const numeric = Number(target);
        return (list ?? []).find((item: any) => {
          const code = normCode(item?.code);
          return /^\d+$/.test(code) && Number(code) === numeric;
        });
      };

      // 입력이 하나도 없는 시트(엑셀 업로드 최초 상태)에서 그냥 통과되는 것을 막기 위해,
      // 이 경우에만 첫 행을 검증 대상으로 삼는다. (행 전체를 오류로 도배하지 않는다)
      const validateFirstRowWhenAllBlank =
        (gridRef.current.api as any)?.__validateFirstRowWhenAllBlank ?? false;
      const rowHasInput = (row: any) =>
        !!row &&
        inputFields.some((f) => {
          const value = row[f];
          return (
            value !== null && value !== undefined && String(value).trim() !== ""
          );
        });
      let isAllBlank = false;
      if (validateFirstRowWhenAllBlank) {
        isAllBlank = true;
        gridRef.current.api.forEachNode((node) => {
          if (node.rowPinned || !node.data) return;
          if (rowHasInput(node.data)) isAllBlank = false;
        });
      }

      gridRef.current.api.forEachNode((node) => {
        const rowIndex = node.rowIndex;
        const rowData = node.data;
        const isChanged =
          !changedOnly ||
          changedRows.has(rowData) ||
          (rowData?.KeyField && changedKeys.has(String(rowData.KeyField)));
        if (node.rowPinned) return; //  푸터 행은 오류 체크 제외

        if (rowIndex === null || rowIndex < 0) return; // Footer 행 무시

        if (!rowData) return;

        //  엑셀 내부 중복(PK) 체크
        const pkKey = makePkKey(rowData);
        // PK가 모두 빈값인 경우는 중복 검사 대상 제외
        const isPkAllBlank =
          pkKey === "" ||
          compareFields.every(
            (f) => rowData[f] == null || String(rowData[f]).trim() === ""
          );
        if (!isPkAllBlank) {
          if (duplicateKeyMap.has(pkKey)) {
            // 이미 동일 PK가 존재: 최초 행과 현재 행 모두 오류 마킹
            const first = duplicateKeyMap.get(pkKey)!;
            const msg = "엑셀 업로드 시 PK가 중복되었습니다(동일 키 중복).";
            if (isChanged || first.isChanged) {
              markDuplicateRow(first.rowIndex, msg);
              markDuplicateRow(rowIndex!, msg);
            }
          } else {
            duplicateKeyMap.set(pkKey, { rowIndex: rowIndex!, isChanged });
          }
        }
        if (changedOnly && !isChanged) return;
        // 성능: 빈 row는 기존데이터 매칭/타입검증 전에 즉시 스킵
        const hasInputDataFast = inputFields.some((f) => {
          const value = rowData[f];
          return (
            value !== null && value !== undefined && String(value).trim() !== ""
          );
        });
        // 전부 빈 시트일 때는 첫 행만 검증해 "그냥 통과"를 막는다.
        const forceValidateBlankRow = isAllBlank && rowIndex === 0;
        if (!hasInputDataFast && !forceValidateBlankRow) {
          // setDataValue 를 쓰면 편집 이벤트(cellValueChanged)를 타면서
          // 행이 재배치되므로, 표시용 오류내용은 데이터에 직접 반영만 한다.
          rowData.__upload_error_summary = "";
          newErrorMap.delete(rowIndex);
          return;
        }
        const norm = (v: any) => (v == null ? "" : String(v).trim());

        const isExistingCode = codeDataList.some((item) => {
          return compareFields.every((field) => {
            const rowValue = norm(rowData[field]);
            const itemValue = norm(item[field]);

            // console.log(
            //   ` Checking field: ${field}, rowData: ${rowValue}, codeDataList: ${itemValue}`
            // );

            return rowValue === itemValue;
          });
        });

        // 입력된 값이 하나라도 있는지 확인

        // 빈 행이면 오류 체크하지 않고 무시

        const rowErrors = {
          fields: new Set<string>(),
          messages: new Map<string, string>(),
        };
        const rowSummarySet = new Set<string>();
        const addRowError = (
          fieldName: string,
          message: string,
          mirrorField?: string
        ) => {
          rowErrors.fields.add(fieldName);
          rowErrors.messages.set(fieldName, message);
          if (mirrorField && mirrorField !== fieldName) {
            rowErrors.fields.add(mirrorField);
            rowErrors.messages.set(mirrorField, message);
          }
          rowSummarySet.add(message);
        };

        resolvedColumnDefs.forEach((column) => {
          const columnDef = column as CustomColDef;
          const field = columnDef.field;

          if (!field) return;
          const value = rowData[field];
          const v = value == null ? "" : String(value).trim();
          const isComboOrCode =
            columnDef.tp_column === "SnComboBox" ||
            columnDef.tp_column === "SnCodeBox";
          const isNmText =
            columnDef.tp_column === "SnTextBox" &&
            typeof field === "string" &&
            field.startsWith("nm_");
          const displayField =
            columnDef.cd_displaycolumn ||
            (field.startsWith("cd_") ? field.replace(/^cd_/, "nm_") : "");
          const dvRaw = displayField ? rowData?.[displayField] : "";
          const dv = dvRaw == null ? "" : String(dvRaw).trim();

          //  필수값 체크
          if (
            columnDef.yn_required === "Y" &&
            !isNmText &&
            ((isComboOrCode && v === "" && dv === "") ||
              (!isComboOrCode && v === ""))
          ) {
            addRowError(
              field,
              `${columnDef.headerName}은(는) 필수 입력 항목입니다.`
            );
          }

          if (
            columnDef.tp_column === "SnNumberBox" &&
            (value === null || value === undefined || value === "")
          ) {
            if (updateValues) {
              rowData[field] = 0; // 기본값 0으로 세팅
            }
          }

          if (
            field.startsWith("dt_") &&
            typeof value === "string" &&
            value.trim() !== ""
          ) {
            const tpFormat = (columnDef as any).tp_format;

            const normalized =
              parseToyyyyMMdd(value, tpFormat) ??
              // 붙여넣기에서 yyyyMMdd 로 바꿔 넣은 값, 붙여넣기 변환을 못 탄 값도 같은 규칙으로 받는다
              parseToyyyyMMddSnSheet(value, tpFormat);
            if (!normalized) {
              // 유효하지 않으면 에러 처리
              addRowError(
                field,
                `${columnDef.headerName} 값이 유효하지 않습니다.`
              );
            } else if (updateValues) {
              //  유효하고 updateValues가 true이면 yyyyMMdd로 변환해서 rowData에 반영
              rowData[field] = normalized;
            } else if (
              menuInfo &&
              menuInfo.cd_menu === "SN_SAL_SAO_REG_001_TEX" &&
              field === "dt_dlv"
            ) {
              const noPo = rowData.no_po;
              if (!poDtMap.has(noPo)) {
                // 같은 no_po의 첫 행
                poDtMap.set(noPo, normalized);
              } else if (poDtMap.get(noPo) !== normalized) {
                // 같은 no_po인데 dt_dlv가 다르면 오류
                addRowError(
                  field,
                  `${columnDef.headerName} 값은 동일 PO(${noPo}) 내에서 동일해야 합니다.`
                );
              }
            }
          }
          //  SnComboBox 유효성 검사
          if (
            columnDef.tp_column === "SnComboBox" &&
            columnDef.cd_clas &&
            value
          ) {
            const validCodes =
              codeDataCacheRef.current.get(columnDef.cd_clas) || [];

            const matchingItem = findCodeItem(validCodes, value);

            if (!matchingItem) {
              // 유효하지 않은 값 처리
              addRowError(
                field,
                `${columnDef.headerName} 값이 유효하지 않습니다.`
              );
            } else {
              //  유효한 값이면 컬럼 데이터 업데이트
              if (updateValues) {
                rowData[field] = matchingItem.code;

                const displayField =
                  columnDef.cd_displaycolumn ||
                  (field.startsWith("cd_")
                    ? field.replace(/^cd_/, "nm_")
                    : undefined);

                if (displayField && displayField !== field) {
                  rowData[displayField] = matchingItem.name;
                }
              }
            }
          }

          //  SnCodeBox 유효성 검사
          if (
            columnDef.tp_column === "SnCodeBox" &&
            columnDef.cd_popup &&
            value
          ) {
            const validPopups =
              popupDataCacheRef.current.get(columnDef.cd_popup) || [];
            const matchingItem = findCodeItem(validPopups, value);

            if (!matchingItem) {
              addRowError(
                field,
                `${columnDef.headerName} 값이 유효하지 않습니다.`
              );
            } else {
              if (updateValues) {
                /* 20250910 이부분은 분기로 처리 저장시에만 변경 */
                // 유효한 값이면 컬럼 데이터 업데이트
                rowData[field] = matchingItem.code; // 코드 업데이트
                const displayField =
                  columnDef.cd_displaycolumn || field.replace(/^cd_/, "nm_");
                rowData[displayField] = matchingItem.name; // 이름 업데이트
              }
            }
          }
        });

        // 오류가 있으면 `newErrorMap`에 추가
        // Validate 중 생성된 셀 오류 메시지를 행 단위로 합쳐 오류내용 컬럼에 그대로 표시
        const rowSummary = Array.from(rowSummarySet).join(" | ");
        // 위와 동일 - 편집 이벤트를 타지 않도록 데이터만 갱신하고,
        // 화면 반영은 아래의 refreshCells 로 처리한다.
        rowData.__upload_error_summary = rowSummary;

        if (rowErrors.fields.size > 0) {
          // 기존 셀 오류 표시 로직(errorMap.fields)을 그대로 쓰면서
          // 오류내용 컬럼도 동일하게 색/툴팁 대상에 포함한다.
          newErrorMap.set(rowIndex, rowErrors);
          hasErrors = true;
        } else {
          // 해당 행의 오류가 해결되었으면 삭제
          newErrorMap.delete(rowIndex);
        }
        const updatedRow = { ...rowData, cd_firm: userInfo.cd_firm };

        if (isExistingCode) {
          modifiedRows.current.updated.push(updatedRow);
        } else {
          modifiedRows.current.added.push(updatedRow);
        }
      });

      // 오류항목은 툴팁과 동일한 소스(messages)만 사용해 생성
      //  기존 오류 데이터 삭제 후 새로운 오류 맵을 설정 (완전히 덮어쓰기)
      setErrorMap(new Map(newErrorMap));

      // **errorMap 업데이트 후 컬럼 스타일 및 툴팁 적용**

      // 즉시 화면 반영
      gridRef.current?.api?.refreshCells({ force: true });

      //  오류가 없는 경우 true 반환
      return !hasErrors;
    } finally {
      // 어떤 경로든 무조건 로딩 해제
      if (!suppressLoading && !keepOverlayUntilCallerDone) {
        hideLoading();
      }
      // 셀 스타일/툴팁 갱신 후 잔상 있을 때만 강제 리프레시
    }
  };
  const suppressIMEInput = (params: any) => {
    const event = params.event as KeyboardEvent;
    return event.isComposing; // IME 입력 중이면 이벤트 무시
  };

  const handleCellEditingStarted = (event: any) => {
    setTimeout(() => {
      const inputElement = document.querySelector(".ag-cell-editor");
      if (inputElement) {
        (inputElement as HTMLInputElement).setAttribute("inputmode", "text");
      }
    }, 0);
  };

  const handleCellKeyDown1 = (event: CellKeyDownEvent) => {
    const keyboardEvent = event.event as KeyboardEvent; // Type Casting

    if (keyboardEvent?.isComposing) {
      keyboardEvent.stopPropagation();
    }
  };

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    api: gridApiRef.current, // AG Grid API 노출
    columnApi: columnApiRef.current, // AG Grid Column API 노출
    selectAndFocusRow,
    selectAllFiltered,
    saveColumnState,
    restoreColumnState,
    getSelectedRow,
    getSelectedRows,
    onBeforeAddRow,
    addRow,
    deleteSelectedRows,
    deleteRowByIndex,
    deleteAllRows,
    getChange,
    requiredValuesCheck,
    resetUpdate,
    footer: getFooterData(),
    getFooterDataAsync,
    applyFilters,
    resetFilters,
    sharedCellValueChanged,
    suppressEventDispatch,
    allowEventDispatch,
    setValue: (rowNode: RowNode, column: string, value: any) =>
      setValue(rowNode, column, value),
    setValueDrill: (rowNode: RowNode, column: string, value: any) =>
      setValueDrill(rowNode, column, value),
    resetGridData,
    validateData,
    setLoadingMessage,
    setCodeDataList: (newData: Record<string, string>[]) => {
      setcheckCodeData(newData); // pk체크용 데이터 mas_item, mas_cust ...
    },
  }));

  //#endregion

  const renderGridContent = () => {
    return (
      <div
        className="SnSheet"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        {isFilterVisible && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="text"
              id="filter-text-box"
              defaultValue={filterTextRef.current}
              onInput={onFilterTextBoxChanged}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  findCellAndFocus();
                }
              }}
            />
            <button
              onClick={closeFilterBox}
              style={{
                marginLeft: "5px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              &#10005;
            </button>
          </div>
        )}
        {/* 그리드 */}
        <div className="ag-theme-alpine" style={{ flex: "1 1 auto", ...style }}>
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            headerHeight={30} // header 높이 설정
            getRowHeight={() => {
              return 30;
            }} // cell, footer 높이 설정
            gridOptions={gridOptionsExt}
            ref={gridRef}
            onGridReady={handleGridReady}
            columnDefs={columnDefsWithRowNumber}
            rowData={rowData}
            // 행 식별자를 지정하지 않으면 applyTransaction/rowData 갱신이 객체 참조로만
            // 매칭돼 행이 제자리에서 갱신되지 못하고 순서가 바뀐다(맨 아래로 이동).
            getRowId={(params: any) => {
              if (!params?.data?.KeyField) {
                params.data.KeyField = generateKey();
              }
              return String(params.data.KeyField);
            }}
            defaultColDef={defaultColDef}
            pinnedBottomRowData={pinnedBottomRowData}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            enableRangeSelection={true}
            animateRows={animateRows}
            cellSelection={
              cellSelection && enableFillHandle
                ? {
                    handle: {
                      mode: "fill",
                      // SnGrid 와 같게: 마지막 값을 그대로 복사 (숫자도 증가시키지 않음)
                      setFillValue: (params: any) => params.values.at(-1),
                    },
                  }
                : cellSelection
            }
            suppressNoRowsOverlay={suppressNoRowsOverlay}
            suppressMenuHide={suppressMenuHide}
            rowGroupPanelShow={rowGroupPanelShow}
            enableCharts={enableExternalChart}
            localeText={localeText}
            rowSelection={rowSelection}
            // onCellEditingStarted={handleCellEditingStarted}
            // stopEditingWhenCellsLoseFocus={false}
            // onCellKeyDown={handleCellKeyDown1}
            // viewportDatasource={rowData}
            // domLayout="normal"
            // suppressHorizontalScroll={false}
            // suppressRowVirtualisation={false}
            // rowDragManaged={rowDragManaged}
            // suppressRowClickSelection={true}
            // suppressFocusAfterRefresh={true}
            // suppressCellFocus={false}
            // suppressRowTransform={true} //CSS Transform 애니메이션 비활성화
            // stopEditingWhenCellsLoseFocus={true} // grid 외부 클릭 시 editing 비활성화

            // onRowClicked={handleInternalonRowClicked}
            // onRowDoubleClicked={handleOnRowDoubleClicked}
            // onCellDoubleClicked={handleCellDoubleClick}
            // onCellValueChanged={handleInternalCellValueChanged}

            // onCellFocused={handleCellFocused} //포커스 변경 후 데이터 처리
            // onCellKeyDown={handleCellKeyDown} // 키보드 입력 처리
            // popupParent={popupParent} // 우클릭 크기 그리드 고정 제외

            // onColumnMoved={handleColumnStateChange}
            // onColumnResized={handleColumnStateChange}
            // onColumnPinned={handleColumnStateChange}
            // onColumnVisible={handleColumnStateChange}
            // onSelectionChanged={() =>
            //   handleInternalSelectionChanged(ref, onSelectionChanged)
            // } //  추가

            {...gridOptions}
            // onRowDragEnd={handleRowDragEnd}
            // autoGroupColumnDef={autoGroupColumnDef}
            suppressClipboardPaste={false} // 붙여넣기 금지 해제
            enableCellTextSelection={false} //  반드시 false로 설정
            processDataFromClipboard={handlePasteData} //  이벤트 추가
            tooltipShowDelay={0} // 즉시 표시
            tooltipHideDelay={3000} // 3초 후 사라짐
          />
        </div>

        {/* 독립 차트 */}
        {enableExternalChart && (
          <div
            ref={chartContainerRef}
            style={{
              marginTop: "20px",
              height: "300px",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            {/* 독립 차트가 렌더링됩니다 */}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {title ? (
        <SnTab
          titles={[title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={onBtnAddClick}
              onBtnDeleteClick={onBtnDeleteClick}
              body={header}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
});
export default React.memo(SnSheet);
